import React, {useEffect, useState} from 'react';
import {Project} from "../interfaces/interfaces";
import {Button, Card} from 'flowbite-react';
import {GoSortAsc} from "react-icons/go";
import {GoSortDesc} from "react-icons/go";
import {useNavigate} from "react-router-dom";
import {IoArrowBackOutline} from "react-icons/io5";

function OgAge() {
    const [state, setState] = useState({
        projects: [],
        sortAsc: true,
        displayedItems: 20,
    });

    const navigate = useNavigate();

    useEffect(() => {
        const fetchAndSortProjects = async () => {
            try {
                const response = await fetch(process.env.PUBLIC_URL + '/projects.json');

                const data = await response.json();

                const activeProjects = data.filter((project: Project) => project.archived === undefined || !project.archived);

                let sortedProjects = activeProjects.filter((project: Project) => project.establishmentDate);

                sortedProjects.sort((a: any, b: any) => {
                    const dateA: any = new Date(a.establishmentDate);
                    const dateB: any = new Date(b.establishmentDate);
                    return state.sortAsc ? dateA - dateB : dateB - dateA;
                });
                setState(prevState => ({
                    ...prevState,
                    projects: sortedProjects.slice(0, state.displayedItems)
                }));

            } catch (error) {
                console.error('Failed to fetch projects', error);
            }
        };

        fetchAndSortProjects();
    }, [state.sortAsc, state.displayedItems]); // Dependencies

    const toggleSortDirection = () => {
        setState(prevState => ({
            ...prevState,
            sortAsc: !prevState.sortAsc
        }));
    };

    const handleLoadMore = () => {
        setState(prevState => ({
            ...prevState,
            displayedItems: prevState.displayedItems + state.displayedItems
        }));
    };

    const formatDate = (dateString: string) => {
        const options: any = {month: 'long', year: 'numeric'};
        return new Date(dateString).toLocaleDateString('en-US', options);
    };

    const calculateMonthsAgo = (dateString: string) => {
        const startDate: any = new Date(dateString);
        const endDate: any = new Date();
        const yearDiff = endDate.getFullYear() - startDate.getFullYear();
        const monthDiff = endDate.getMonth() - startDate.getMonth();
        const dayDiff = endDate.getDate() - startDate.getDate();

        let months = yearDiff * 12 + monthDiff;
        // Adjust for cases where the end date's day is less than the start date's day
        if (dayDiff < 0) {
            months--;
        }

        if (months > 0) {
            return `${months} months ago ${getAgeEmoji(months)}`;
        } else {
            // Calculate the number of days if the months difference is zero or negative
            const days = Math.round((endDate - startDate) / (1000 * 60 * 60 * 24));
            return days > 0 ? `${days} days ago ${getAgeEmoji(0)}` : `Today ${getAgeEmoji(0)}`;
        }
    };

    const getAgeEmoji = (months: number) => {
        if (months >= 24) {
            return '🦖'; // Old man emoji for more than 2 years
        } else if (months >= 18) {
            return '👴'; // Young man emoji for less than 2 years but more than 1 year
        } else if (months >= 12) {
            return '🧑'; // Young man emoji for less than 2 years but more than 1 year
        } else {
            return '👶'; // Child emoji for less than 1 year
        }
    };

    const navigateTo = (url: string) => {
        navigate(url);
    };

    return (
        <div className="App">
            <header className="App-header">
                <div className="container flex flex-col items-center">
                    <div className="header flex flex-row items-center justify-center mb-6">
                        <h1 className="text-4xl uppercase">OG METER</h1>
                        <img
                            className="ml-4"
                            width={50}
                            height={50}
                            src="/images/ruler.png"
                            alt="Ruler Logo"
                        />
                    </div>
                    <div className="flex justify-between w-full">
                        <Button outline gradientDuoTone="purpleToBlue" onClick={() => navigateTo('/')}>
                            <div className="flex flex-row items-center justify-between">
                                 <span className="text-xl ml-2">
                                    <IoArrowBackOutline/>
                                </span>
                                <span>
                                    To main
                                </span>
                            </div>
                        </Button>
                        <Button gradientDuoTone="purpleToBlue" onClick={toggleSortDirection}>
                            <div className="flex flex-row items-center justify-between">
                           <span>
                                {state.sortAsc ? `Old Dinosaurs First` : `Newborn Stars First`}
                           </span>
                                <span className="text-xl ml-2">
                                {state.sortAsc ? <GoSortAsc/> : <GoSortDesc/>}
                            </span>
                            </div>
                        </Button>
                    </div>
                    <div className="mt-4 w-full">
                        {state.projects.map((project: Project, index: number) => (
                            <Card key={index} className={`ogage-card mb-4 flex flex-row items-center gap-4}`}>
                                <div className="flex-shrink-0">
                                    {project.bannerUrl && (
                                        <img
                                            src={project.logoUrl ? project.logoUrl : project.bannerUrl}
                                            alt={project.projectName}
                                            className="w-24 h-24 object-cover rounded-full"
                                        />
                                    )}
                                </div>
                                <div className="flex-grow text-left">
                                    <h2 className="text-blue-900 uppercase card-header truncate">{index + 1}.{project.projectName}</h2>
                                    <p className="text-blue-900 card-text">Established: {formatDate(project.establishmentDate)}</p>
                                    <p className="text-blue-900 card-text">{calculateMonthsAgo(project.establishmentDate)}</p>
                                </div>

                            </Card>
                        ))}
                    </div>
                    {state.displayedItems <= state.projects.length && (
                        <Button gradientDuoTone="tealToLime" onClick={handleLoadMore} className="mt-4">
                            Load More
                        </Button>
                    )}
                </div>
            </header>
        </div>
    );
}

export default OgAge;
